import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../global/functions/apiClient";
const apiUrl = process.env.REACT_APP_API_URL;

const initialState = {
  status: "idle",
  error: null,
  loading: false,
  userData: null,
  isAuthenticated: false,
  // Whether the initial session restore (fetchAuthUser on app load) has
  // resolved yet — routing must not make redirect decisions before this is
  // true, or a logged-in user gets bounced to /signin for a moment on every
  // refresh while that request is still in flight.
  authChecked: false,
};

export const login = createAsyncThunk("auth/authLogin", async (body) => {
  try {
    const response = await apiClient.post(`${apiUrl}/auth/login`, body, {
      headers: {
        "app-type": "dashboard",
      },
    });
    return response?.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message);
  }
});

export const fetchAuthUser = createAsyncThunk(
  "auth/fetchAuthUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${apiUrl}/auth/me`);
      return response?.data?.user || null;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async () => {
  try {
    await apiClient.post(`${apiUrl}/auth/logout`);
  } catch (err) {
    // Clear local session state regardless of whether the server call
    // succeeded — the user asked to log out either way.
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logOut: (state) => {
      state.userData = null;
      state.isAuthenticated = false;
    },
    updateStatus: (state) => {
      state.status = "idle";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "logedIn";
        state.loading = false;
        state.userData = action.payload.user || null;
        state.isAuthenticated = true;
        state.authChecked = true;
        state.success = action.payload.message;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "error";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAuthUser.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.isAuthenticated = !!action.payload;
        state.authChecked = true;
      })
      .addCase(fetchAuthUser.rejected, (state) => {
        state.userData = null;
        state.isAuthenticated = false;
        state.authChecked = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.userData = null;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state) => {
        state.userData = null;
        state.isAuthenticated = false;
      });
  },
});

export const { logOut, updateStatus } = authSlice.actions;
export const getAuthStatus = (state) => state.auth.status;
export const getAuthError = (state) => state.auth.error;
export const getAuthSuccess = (state) => state.auth.success;
export const getAuthLoading = (state) => state.auth.loading;
export const selectAuthUser = (state) => state.auth.userData;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthChecked = (state) => state.auth.authChecked;
export default authSlice.reducer;
